import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ArrowLeft,
  Check,
  Eye,
  EyeOff,
  GraduationCap,
  Lock,
  Mail,
  User as UserIcon,
  Users,
} from 'lucide-react';
import { PASSWORD_MIN_LENGTH, ROLE_HOME_ROUTE } from '@tutor/shared';
import { registerSchema, type RegisterInput } from '@tutor/validation';
import { Alert, Button, Input, cn } from '@tutor/ui';
import { ApiError } from '../../lib/api-client';
import { useAuthStore } from '../../stores/auth.store';

type Role = 'TUTOR' | 'STUDENT';

const ROLE_OPTIONS: Array<{
  value: Role;
  title: string;
  blurb: string;
  icon: typeof Users;
}> = [
  {
    value: 'STUDENT',
    title: "I'm a student",
    blurb: 'Join lessons, complete homework and track your progress.',
    icon: GraduationCap,
  },
  {
    value: 'TUTOR',
    title: "I'm a tutor",
    blurb: 'Teach lessons, set and mark homework, and monitor your students.',
    icon: Users,
  },
];

const EDUCATION_LEVELS = [
  { value: 'KS2', label: 'Key Stage 2' },
  { value: 'KS3', label: 'Key Stage 3' },
  { value: 'GCSE', label: 'GCSE' },
  { value: 'A_LEVEL', label: 'A-Level' },
  { value: 'IB', label: 'International Baccalaureate' },
  { value: 'UNIVERSITY', label: 'University' },
  { value: 'OTHER', label: 'Other' },
] as const;

/** Live feedback against the same rules the server enforces. */
function passwordChecks(value: string) {
  return [
    { label: `At least ${PASSWORD_MIN_LENGTH} characters`, ok: value.length >= PASSWORD_MIN_LENGTH },
    { label: 'An uppercase letter', ok: /[A-Z]/.test(value) },
    { label: 'A lowercase letter', ok: /[a-z]/.test(value) },
    { label: 'A number', ok: /[0-9]/.test(value) },
    { label: 'A symbol', ok: /[^A-Za-z0-9]/.test(value) },
  ];
}

export function RegisterPage() {
  const registerAccount = useAuthStore((state) => state.register);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // The landing page CTAs deep-link a role, so those visitors skip the picker.
  const presetRole = searchParams.get('role');
  const initialRole: Role | null =
    presetRole === 'TUTOR' || presetRole === 'STUDENT' ? presetRole : null;

  const [step, setStep] = useState<'role' | 'details'>(initialRole ? 'details' : 'role');
  const [role, setRole] = useState<Role | null>(initialRole);
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: initialRole ?? 'STUDENT',
      acceptedTerms: false as unknown as true,
    },
  });

  const password = watch('password') ?? '';
  const checks = passwordChecks(password);

  const chooseRole = (value: Role): void => {
    setRole(value);
    setValue('role', value, { shouldValidate: true });
    setStep('details');
  };

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      const user = await registerAccount({
        ...values,
        // The browser knows the user's timezone; asking for it would be noise.
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      navigate(ROLE_HOME_ROUTE[user.role] ?? '/', { replace: true });
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.details) {
          for (const [field, messages] of Object.entries(error.details)) {
            if (field in values) {
              setError(field as keyof RegisterInput, { message: messages[0] });
            }
          }
        }
        setFormError(error.message);
        return;
      }
      setFormError('We could not reach the server. Please check your connection and try again.');
    }
  });

  return (
    <div className="flex min-h-screen">
      <div className="relative hidden w-1/2 flex-col justify-between bg-brand-700 p-12 text-white lg:flex">
        <Link to="/" className="flex w-fit items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15">
            <GraduationCap className="h-5 w-5" aria-hidden />
          </span>
          <span className="text-lg font-semibold tracking-tight">Tutor Platform</span>
        </Link>

        <div className="max-w-md">
          <h2 className="text-3xl font-semibold leading-tight tracking-tight">
            Create your account in under a minute.
          </h2>
          <p className="mt-4 text-brand-100">
            Tutors get scheduling, homework marking and progress tracking. Students get
            their timetable, their work and their feedback — all in one place.
          </p>
        </div>

        <p className="text-sm text-brand-200">
          © {new Date().getFullYear()} Tutor Platform. All rights reserved.
        </p>
      </div>

      <div className="flex w-full items-center justify-center px-6 py-12 lg:w-1/2">
        <div className="w-full max-w-sm">
          {step === 'role' ? (
            <>
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
                Create an account
              </h1>
              <p className="mt-1.5 text-sm text-slate-500">
                First, tell us how you will be using the platform.
              </p>

              <div className="mt-6 space-y-3">
                {ROLE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => chooseRole(option.value)}
                    className={cn(
                      'flex w-full items-start gap-3 rounded-xl border p-4 text-left transition-colors',
                      'border-slate-200 bg-white hover:border-brand-300 hover:bg-brand-50/40',
                      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600',
                    )}
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                      <option.icon className="h-5 w-5" aria-hidden />
                    </span>
                    <span className="min-w-0">
                      <span className="block font-medium text-slate-900">{option.title}</span>
                      <span className="mt-0.5 block text-sm text-slate-500">{option.blurb}</span>
                    </span>
                  </button>
                ))}
              </div>

              <p className="mt-8 text-center text-sm text-slate-500">
                Already have an account?{' '}
                <Link to="/login" className="font-medium text-brand-600 hover:text-brand-700">
                  Sign in
                </Link>
              </p>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setStep('role')}
                className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden />
                Change account type
              </button>

              <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
                {role === 'TUTOR' ? 'Tutor account' : 'Student account'}
              </h1>
              <p className="mt-1.5 text-sm text-slate-500">
                Fill in your details to finish setting up.
              </p>

              {formError && (
                <Alert tone="danger" className="mt-6">
                  {formError}
                  {formError.toLowerCase().includes('already exists') && (
                    <>
                      {' '}
                      <Link to="/login" className="font-medium underline">
                        Sign in instead
                      </Link>
                      .
                    </>
                  )}
                </Alert>
              )}

              <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
                <input type="hidden" {...register('role')} />

                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="First name"
                    autoComplete="given-name"
                    placeholder="James"
                    leftIcon={<UserIcon className="h-4 w-4" aria-hidden />}
                    error={errors.firstName?.message}
                    {...register('firstName')}
                  />
                  <Input
                    label="Last name"
                    autoComplete="family-name"
                    placeholder="Smith"
                    error={errors.lastName?.message}
                    {...register('lastName')}
                  />
                </div>

                <Input
                  label="Email address"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  leftIcon={<Mail className="h-4 w-4" aria-hidden />}
                  error={errors.email?.message}
                  {...register('email')}
                />

                <div>
                  <Input
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="••••••••"
                    leftIcon={<Lock className="h-4 w-4" aria-hidden />}
                    error={errors.password?.message}
                    rightSlot={
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="rounded p-1.5 text-slate-400 hover:text-slate-600"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    }
                    {...register('password')}
                  />

                  {password.length > 0 && (
                    <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1">
                      {checks.map((check) => (
                        <li
                          key={check.label}
                          className={cn(
                            'flex items-center gap-1 text-xs',
                            check.ok ? 'text-emerald-600' : 'text-slate-400',
                          )}
                        >
                          <Check className={cn('h-3 w-3', !check.ok && 'opacity-30')} aria-hidden />
                          {check.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <Input
                  label="Confirm password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="••••••••"
                  leftIcon={<Lock className="h-4 w-4" aria-hidden />}
                  error={errors.confirmPassword?.message}
                  {...register('confirmPassword')}
                />

                {role === 'TUTOR' ? (
                  <Input
                    label="Headline"
                    hint="Optional — shown on your profile."
                    placeholder="A-Level Mathematics specialist"
                    error={errors.headline?.message}
                    {...register('headline')}
                  />
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label
                        htmlFor="educationLevel"
                        className="mb-1.5 block text-sm font-medium text-slate-700"
                      >
                        Education level
                      </label>
                      <select
                        id="educationLevel"
                        className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200"
                        {...register('educationLevel')}
                      >
                        <option value="">Select…</option>
                        {EDUCATION_LEVELS.map((level) => (
                          <option key={level.value} value={level.value}>
                            {level.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <Input
                      label="Year group"
                      placeholder="Year 13"
                      error={errors.yearGroup?.message}
                      {...register('yearGroup')}
                    />
                  </div>
                )}

                <div>
                  <label className="flex cursor-pointer items-start gap-2.5">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                      {...register('acceptedTerms')}
                    />
                    <span className="text-sm text-slate-600">
                      I agree to the terms of service and privacy policy.
                    </span>
                  </label>
                  {errors.acceptedTerms && (
                    <p role="alert" className="mt-1.5 text-sm text-rose-600">
                      {errors.acceptedTerms.message}
                    </p>
                  )}
                </div>

                <Button type="submit" fullWidth size="lg" isLoading={isSubmitting}>
                  Create account
                </Button>
              </form>

              <p className="mt-6 text-center text-sm text-slate-500">
                Already have an account?{' '}
                <Link to="/login" className="font-medium text-brand-600 hover:text-brand-700">
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
